import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { VIBE_CODING_MODELS } from '../../../features/vibe-coding/redux/constants';
import { drapcodeIcon } from '../../../common/custom/icon-library';

const groupModelsByProvider = (models) => {
  const groups = [];
  models.forEach((model) => {
    let group = groups.find((item) => item.providerId === model.provider);
    if (!group) {
      group = { providerId: model.provider, providerLabel: model.providerLabel, models: [] };
      groups.push(group);
    }
    group.models.push(model);
  });
  return groups;
};

const ModelSelector = ({ value, onChange, disabled }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef(null);
  const searchRef = useRef(null);
  const selected = VIBE_CODING_MODELS.find((model) => model.id === value) || VIBE_CODING_MODELS[0];

  useEffect(() => {
    if (!open) return;
    const handlePointerDown = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  useEffect(() => {
    if (open && searchRef.current) {
      searchRef.current.focus();
    }
    if (!open) {
      setQuery('');
    }
  }, [open]);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return VIBE_CODING_MODELS;
    return VIBE_CODING_MODELS.filter((model) =>
      [model.label, model.providerLabel, model.id, model.description]
        .join(' ')
        .toLowerCase()
        .includes(needle),
    );
  }, [query]);

  const groups = useMemo(() => groupModelsByProvider(filtered), [filtered]);

  const handleSelect = (modelId) => {
    if (onChange) onChange(modelId);
    setOpen(false);
  };

  return (
    <div ref={rootRef} style={{ position: 'relative' }}>
      <button
        type="button"
        disabled={disabled}
        onClick={(e) => {
          e.stopPropagation();
          if (!disabled) setOpen((prev) => !prev);
        }}
        title={selected ? `${selected.providerLabel} · ${selected.label}` : 'Select model'}
        aria-haspopup="listbox"
        aria-expanded={open}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          maxWidth: '220px',
          height: '30px',
          padding: '0 10px',
          borderRadius: '999px',
          border: '1px solid #e2e8f0',
          background: open ? '#f1f5f9' : '#f8fafc',
          color: '#334155',
          fontSize: '12px',
          fontWeight: 600,
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.6 : 1,
          whiteSpace: 'nowrap',
        }}
      >
        <i
          className={`wb ${drapcodeIcon('model')}`}
          aria-hidden="true"
          style={{
            fontSize: '13px',
            color: '#64748b',
            flexShrink: 0,
          }}
        />
        <span
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: '150px',
          }}
        >
          {selected ? selected.label : 'Select model'}
        </span>
        <i
          className={`fa fa-chevron-${open ? 'up' : 'down'}`}
          aria-hidden="true"
          style={{ fontSize: '9px', color: '#64748b' }}
        />
      </button>

      {open && !disabled && (
        <div
          role="listbox"
          aria-label="Select model"
          style={{
            position: 'absolute',
            bottom: '36px',
            left: 0,
            width: '300px',
            maxWidth: '82vw',
            background: '#ffffff',
            border: '1px solid #e2e8f0',
            borderRadius: '12px',
            boxShadow: '0 12px 32px rgba(15, 23, 42, 0.16)',
            overflow: 'hidden',
            zIndex: 30,
          }}
        >
          <div
            style={{
              padding: '8px',
              borderBottom: '1px solid #f1f5f9',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            <i
              className={`wb ${drapcodeIcon('search')}`}
              aria-hidden="true"
              style={{ fontSize: '12px', color: '#64748b', marginLeft: '4px' }}
            />
            <input
              ref={searchRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search models…"
              aria-label="Search models"
              style={{
                width: '100%',
                height: '32px',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                padding: '0 10px',
                fontSize: '12px',
                outline: 'none',
                color: '#0f172a',
              }}
            />
          </div>
          <div style={{ maxHeight: '260px', overflowY: 'auto', padding: '6px' }}>
            {groups.length === 0 && (
              <div style={{ padding: '14px', fontSize: '12px', color: '#94a3b8' }}>
                No models match “{query}”.
              </div>
            )}
            {groups.map((group) => (
              <div key={group.providerId} style={{ marginBottom: '4px' }}>
                <div
                  style={{
                    fontSize: '10px',
                    fontWeight: 700,
                    letterSpacing: '0.6px',
                    textTransform: 'uppercase',
                    color: '#94a3b8',
                    padding: '6px 8px 4px',
                  }}
                >
                  {group.providerLabel}
                </div>
                {group.models.map((model) => {
                  const isSelected = selected && selected.id === model.id;
                  return (
                    <button
                      key={model.id}
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSelect(model.id);
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '8px',
                        width: '100%',
                        textAlign: 'left',
                        border: '0',
                        borderRadius: '8px',
                        padding: '8px',
                        background: isSelected ? '#eef2ff' : 'transparent',
                        cursor: 'pointer',
                      }}
                      onMouseEnter={(e) => {
                        if (!isSelected) e.currentTarget.style.background = '#f8fafc';
                      }}
                      onMouseLeave={(e) => {
                        if (!isSelected) e.currentTarget.style.background = 'transparent';
                      }}
                    >
                      <i
                        className={`wb ${drapcodeIcon('model')}`}
                        aria-hidden="true"
                        style={{
                          fontSize: '13px',
                          color: '#64748b',
                          marginTop: '3px',
                          flexShrink: 0,
                        }}
                      />
                      <span style={{ minWidth: 0, flex: 1 }}>
                        <span
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            fontSize: '12px',
                            fontWeight: 600,
                            color: '#0f172a',
                          }}
                        >
                          <span
                            style={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {model.label}
                          </span>
                          {isSelected && (
                            <i
                              className="fa fa-check"
                              aria-hidden="true"
                              style={{ color: '#4f46e5', fontSize: '11px', marginLeft: 'auto' }}
                            />
                          )}
                        </span>
                        <span
                          style={{
                            display: 'block',
                            fontSize: '11px',
                            color: '#64748b',
                            fontFamily: 'ui-monospace, monospace',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {model.id}
                        </span>
                        {model.description && (
                          <span
                            style={{
                              display: 'block',
                              fontSize: '11px',
                              color: '#94a3b8',
                              lineHeight: 1.4,
                            }}
                          >
                            {model.description}
                          </span>
                        )}
                      </span>
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

ModelSelector.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
};

ModelSelector.defaultProps = {
  value: '',
  onChange: null,
  disabled: false,
};

export default ModelSelector;
